import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { API_BASE, authHeaders } from '../../constants/api';
import { Suche, SchließenAlt as Schließen } from '../../icons';
import { WIDGET_TYPES } from './widgetTypes';

function WidgetSettingsPanel({ widget, token, onChange, onDelete, onClose, onLayoutChange }) {
  const typeDef = WIDGET_TYPES[widget.widgetType] || {};
  const fields = typeDef.fields || [];

  /* ── Panel resize ── */
  const panelRef = useRef(null);
  const [panelWidth, setPanelWidth] = useState(320);

  function handleResizeMouseDown(e) {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = panelRef.current?.offsetWidth || 320;

    function onMouseMove(ev) {
      const delta = startX - ev.clientX; // dragging left = panel wider
      setPanelWidth(Math.max(220, Math.min(600, startWidth + delta)));
    }

    function onMouseUp() {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    }

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }

  /* ── State ── */
  const [devices, setDevices] = useState([]);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [availableKeys, setAvailableKeys] = useState([]);
  const [loadingKeys, setLoadingKeys] = useState(false);

  // Search states for device dropdown
  const [deviceSearch, setDeviceSearch] = useState('');
  const [showDeviceSuggestions, setShowDeviceSuggestions] = useState(false);
  const [highlightedDevice, setHighlightedDevice] = useState(-1);
  const deviceSuggestionsRef = useRef(null);

  // Search states for key dropdown
  const [keySearch, setKeySearch] = useState('');
  const [showKeySuggestions, setShowKeySuggestions] = useState(false);
  const [highlightedKey, setHighlightedKey] = useState(-1);
  const keySuggestionsRef = useRef(null);

  const deviceId = widget.deviceId || '';

  // Scroll a highlighted suggestion into view within its list container
  function scrollSuggestionIntoView(listRef, itemId) {
    requestAnimationFrame(() => {
      const item = document.getElementById(itemId);
      if (item && listRef.current) {
        item.scrollIntoView({ block: 'nearest' });
      }
    });
  }

  // Fetch tenant devices on mount
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setLoadingDevices(true);
    fetch(
      `${API_BASE}/tenant/devices?pageSize=50&page=0&sortProperty=name&sortOrder=ASC&type=`,
      { headers: authHeaders(token) }
    )
      .then(r => (r.ok ? r.json() : Promise.reject(r)))
      .then(data => { if (!cancelled) setDevices(data.data || []); })
      .catch(err => console.error('[WidgetSettings] devices fetch error:', err))
      .finally(() => { if (!cancelled) setLoadingDevices(false); });
    return () => { cancelled = true; };
  }, [token]);

  // If the widget's device isn't in the list, fetch it individually
  useEffect(() => {
    if (!deviceId || !token || loadingDevices) return;
    if (devices.some(d => d.id?.id === deviceId)) return;
    fetch(`${API_BASE}/device/${deviceId}`, { headers: authHeaders(token) })
      .then(r => (r.ok ? r.json() : null))
      .then(dev => {
        if (dev) setDevices(prev => {
          if (prev.some(d => d.id?.id === dev.id?.id)) return prev;
          return [dev, ...prev];
        });
      })
      .catch(() => {});
  }, [deviceId, token, devices, loadingDevices]);

  // Set device search text to current device name
  useEffect(() => {
    if (deviceId && devices.length) {
      const dev = devices.find(d => d.id?.id === deviceId);
      if (dev) setDeviceSearch(dev.name || dev.id?.id || '');
    }
  }, [deviceId, devices]);

  // Fetch telemetry + attribute keys when device changes
  useEffect(() => {
    if (!deviceId || !token) { setAvailableKeys([]); return; }
    let cancelled = false;
    setLoadingKeys(true);

    const headers = authHeaders(token);
    const tsUrl = `${API_BASE}/plugins/telemetry/DEVICE/${deviceId}/keys/timeseries`;
    const attrUrl = `${API_BASE}/plugins/telemetry/DEVICE/${deviceId}/keys/attributes`;

    Promise.all([
      fetch(tsUrl, { headers }).then(r => (r.ok ? r.json() : [])),
      fetch(attrUrl, { headers }).then(r => (r.ok ? r.json() : [])),
    ])
      .then(([tsKeys, attrKeys]) => {
        if (!cancelled) {
          const existing = widget.keys || [];
          const merged = [...new Set([...tsKeys, ...attrKeys, ...existing])].sort();
          setAvailableKeys(merged);
        }
      })
      .catch(err => {
        console.error('[WidgetSettings] keys fetch error:', err);
        if (!cancelled) setAvailableKeys([]);
      })
      .finally(() => { if (!cancelled) setLoadingKeys(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId, token]);

  // Close suggestions on outside click
  useEffect(() => {
    function handleClick(e) {
      if (deviceSuggestionsRef.current && !deviceSuggestionsRef.current.parentElement.contains(e.target)) {
        setShowDeviceSuggestions(false);
      }
      if (keySuggestionsRef.current && !keySuggestionsRef.current.parentElement.contains(e.target)) {
        setShowKeySuggestions(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  /* ── Filtered lists ── */
  const filteredDevices = devices.filter(d => {
    const name = (d.name || '').toLowerCase();
    return name.includes(deviceSearch.toLowerCase());
  });

  const selectedKeys = useMemo(() => widget.keys || [], [widget.keys]);
  const filteredKeys = availableKeys.filter(k =>
    !selectedKeys.includes(k) && k.toLowerCase().includes(keySearch.toLowerCase())
  );

  /* ── Handlers ── */
  const selectDevice = useCallback((dev) => {
    const newId = dev.id?.id || '';
    setDeviceSearch(dev.name || newId);
    setShowDeviceSuggestions(false);
    setHighlightedDevice(-1);
    const changes = { deviceId: newId, keys: [], keyUnits: {}, keyLabels: {} };
    if (typeDef.fixedKey) changes.keys = [typeDef.fixedKey];
    onChange(changes);
  }, [onChange, typeDef.fixedKey]);

  const handleDeviceKeyDown = useCallback((e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!showDeviceSuggestions) {
        setShowDeviceSuggestions(true);
        setHighlightedDevice(0);
        return;
      }
      setHighlightedDevice(h => {
        const next = Math.min(h + 1, filteredDevices.length - 1);
        scrollSuggestionIntoView(deviceSuggestionsRef, `ws-device-sug-${next}`);
        return next;
      });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedDevice(h => {
        const next = Math.max(h - 1, 0);
        scrollSuggestionIntoView(deviceSuggestionsRef, `ws-device-sug-${next}`);
        return next;
      });
    } else if (e.key === 'Enter' && highlightedDevice >= 0 && showDeviceSuggestions) {
      e.preventDefault();
      selectDevice(filteredDevices[highlightedDevice]);
    } else if (e.key === 'Escape') {
      setShowDeviceSuggestions(false);
      setHighlightedDevice(-1);
    } else if (e.key === 'Tab') {
      setShowDeviceSuggestions(false);
      setHighlightedDevice(-1);
    }
  }, [showDeviceSuggestions, filteredDevices, highlightedDevice, selectDevice]);

  const selectKey = useCallback((keyName) => {
    if (typeDef.multiKey) {
      onChange({ keys: [...selectedKeys, keyName] });
    } else {
      onChange({ keys: [keyName] });
    }
    setKeySearch('');
    setShowKeySuggestions(false);
    setHighlightedKey(-1);
  }, [typeDef.multiKey, onChange, selectedKeys]);

  function removeKey(keyName) {
    onChange({ keys: selectedKeys.filter(k => k !== keyName) });
  }

  const handleKeyKeyDown = useCallback((e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!showKeySuggestions) {
        setShowKeySuggestions(true);
        setHighlightedKey(0);
        return;
      }
      setHighlightedKey(h => {
        const next = Math.min(h + 1, filteredKeys.length - 1);
        scrollSuggestionIntoView(keySuggestionsRef, `ws-key-sug-${next}`);
        return next;
      });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedKey(h => {
        const next = Math.max(h - 1, 0);
        scrollSuggestionIntoView(keySuggestionsRef, `ws-key-sug-${next}`);
        return next;
      });
    } else if (e.key === 'Enter' && highlightedKey >= 0 && showKeySuggestions) {
      e.preventDefault();
      selectKey(filteredKeys[highlightedKey]);
    } else if (e.key === 'Escape') {
      setShowKeySuggestions(false);
      setHighlightedKey(-1);
    } else if (e.key === 'Tab') {
      setShowKeySuggestions(false);
      setHighlightedKey(-1);
    }
  }, [showKeySuggestions, filteredKeys, highlightedKey, selectKey]);

  function handleTitleChange(e) {
    onChange({ title: e.target.value });
  }

  function handleSettingChange(fieldKey, value) {
    onChange({ settings: { ...widget.settings, [fieldKey]: value } });
  }

  function handleMultiselectToggle(fieldKey, optionValue) {
    const current = widget.settings?.[fieldKey] || [];
    const next = current.includes(optionValue)
      ? current.filter(v => v !== optionValue)
      : [...current, optionValue];
    handleSettingChange(fieldKey, next);
  }

  return (
    <div className="widget-settings-panel" ref={panelRef} style={{ width: `min(${panelWidth}px, 45%)` }}>
      <div className="widget-settings-resize-handle" onMouseDown={handleResizeMouseDown} />
      <div className="widget-settings-header">
        <h3>{typeDef.icon} {typeDef.label || 'Widget'} bearbeiten</h3>
        <button className="widget-settings-close" onClick={onClose} aria-label="Panel schließen">✕</button>
      </div>

      <div className="widget-settings-body">
        {/* Titel */}
        {!typeDef.noTitle && (
          <div className="ws-form-group">
            <input
              type="text"
              className="ws-form-input"
              id={`ws-title-${widget.id}`}
              value={widget.title}
              onChange={handleTitleChange}
              placeholder=" "
            />
            <label className="ws-form-label" htmlFor={`ws-title-${widget.id}`}>Titel</label>
          </div>
        )}

        {/* Datenquelle – Gerät mit Suchfeld */}
        {!typeDef.noDatasource && (
          <div className="ws-form-group ws-search-group">
            <label className="ws-form-label" htmlFor={`ws-device-${widget.id}`}>Datenquelle (Gerät)</label>
            <div className="ws-input-wrapper">
              <span className="ws-search-icon"><Suche /></span>
              <input
                type="text"
                className="ws-form-input ws-search-input"
                id={`ws-device-${widget.id}`}
                value={deviceSearch}
                onChange={e => {
                  setDeviceSearch(e.target.value);
                  setShowDeviceSuggestions(true);
                  setHighlightedDevice(-1);
                }}
                onFocus={() => setShowDeviceSuggestions(true)}
                onKeyDown={handleDeviceKeyDown}
                placeholder="Gerät suchen…"
                autoComplete="off"
                aria-autocomplete="list"
                aria-controls={`ws-device-suggestions-${widget.id}`}
                aria-activedescendant={
                  highlightedDevice >= 0 && showDeviceSuggestions
                    ? `ws-device-sug-${highlightedDevice}`
                    : undefined
                }
              />
              {deviceSearch && (
                <button
                  className="ws-search-clear"
                  onClick={() => {
                    setDeviceSearch('');
                    setShowDeviceSuggestions(false);
                    onChange({ deviceId: '', keys: [], keyUnits: {}, keyLabels: {} });
                  }}
                  aria-label="Eingabe zurücksetzen"
                  title="Eingabe zurücksetzen"
                ><Schließen /></button>
              )}
              {showDeviceSuggestions && filteredDevices.length > 0 && (
                <div
                  id={`ws-device-suggestions-${widget.id}`}
                  className="ws-suggestions"
                  role="listbox"
                  ref={deviceSuggestionsRef}
                >
                  {loadingDevices ? (
                    <div className="ws-suggestion ws-suggestion--disabled">Geräte werden geladen…</div>
                  ) : (
                    filteredDevices.map((d, idx) => (
                      <div
                        key={d.id?.id}
                        id={`ws-device-sug-${idx}`}
                        className={`ws-suggestion${highlightedDevice === idx ? ' ws-suggestion--highlighted' : ''}${d.id?.id === deviceId ? ' ws-suggestion--selected' : ''}`}
                        role="option"
                        aria-selected={highlightedDevice === idx}
                        onMouseDown={() => selectDevice(d)}
                        onMouseEnter={() => setHighlightedDevice(idx)}
                      >
                        {d.name || d.id?.id}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Datenschlüssel */}
        {!typeDef.noDatasource && deviceId && (
          typeDef.fixedKey ? (
            <div className="ws-form-group">
              <span className="ws-section-label">Datenschlüssel</span>
              <div className="ws-fixed-key-info">{typeDef.fixedKey} <em>(fest)</em></div>
            </div>
          ) : (
            <div className="ws-form-group ws-search-group">
              <label className="ws-form-label" htmlFor={`ws-key-${widget.id}`}>
                Datenschlüssel {typeDef.multiKey ? '(mehrere möglich)' : ''}
              </label>
              <div className="ws-input-wrapper">
                <span className="ws-search-icon"><Suche /></span>
                <input
                  type="text"
                  className="ws-form-input ws-search-input"
                  id={`ws-key-${widget.id}`}
                  value={keySearch}
                  onChange={e => {
                    setKeySearch(e.target.value);
                    setShowKeySuggestions(true);
                    setHighlightedKey(-1);
                  }}
                  onFocus={() => setShowKeySuggestions(true)}
                  onKeyDown={handleKeyKeyDown}
                  placeholder="Schlüssel suchen…"
                  autoComplete="off"
                  aria-autocomplete="list"
                  aria-controls={`ws-key-suggestions-${widget.id}`}
                  aria-activedescendant={
                    highlightedKey >= 0 && showKeySuggestions
                      ? `ws-key-sug-${highlightedKey}`
                      : undefined
                  }
                />
                {keySearch && (
                  <button
                    className="ws-search-clear"
                    onClick={() => { setKeySearch(''); setHighlightedKey(-1); }}
                    aria-label="Eingabe zurücksetzen"
                    title="Eingabe zurücksetzen"
                  ><Schließen /></button>
                )}
                {showKeySuggestions && (
                  <div
                    id={`ws-key-suggestions-${widget.id}`}
                    className="ws-suggestions"
                    role="listbox"
                    ref={keySuggestionsRef}
                  >
                    {loadingKeys ? (
                      <div className="ws-suggestion ws-suggestion--disabled">Lade Schlüssel…</div>
                    ) : filteredKeys.length ? (
                      filteredKeys.map((k, idx) => (
                        <div
                          key={k}
                          id={`ws-key-sug-${idx}`}
                          className={`ws-suggestion${highlightedKey === idx ? ' ws-suggestion--highlighted' : ''}`}
                          role="option"
                          aria-selected={highlightedKey === idx}
                          onMouseDown={() => selectKey(k)}
                          onMouseEnter={() => setHighlightedKey(idx)}
                        >
                          {k}
                        </div>
                      ))
                    ) : (
                      <div className="ws-suggestion ws-suggestion--disabled">
                        {keySearch ? 'Kein Treffer' : 'Keine Schlüssel verfügbar'}
                      </div>
                    )}
                  </div>
                )}
              </div>
              {/* Selected badges */}
              {selectedKeys.length > 0 && (
                <div className="ws-selected-badges">
                  {selectedKeys.map(k => (
                    <span key={k} className="ws-badge">
                      {k}
                      <button
                        type="button"
                        className="ws-badge-remove"
                        onClick={() => removeKey(k)}
                        aria-label={`${k} entfernen`}
                        title={`${k} entfernen`}
                      ><Schließen /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )
        )}

        {/* Per-key Bezeichnung + Einheit (Diagramme + Tabelle) */}
        {typeDef.hasKeyLabels && !typeDef.noDatasource && selectedKeys.length > 0 && (
          <div className="ws-key-meta-section">
            <span className="ws-section-label">Schlüssel-Details</span>
            <div className="ws-key-meta-table">
              <div className="ws-key-meta-header">
                <span>Schlüssel</span>
                <span>Bezeichnung</span>
                <span>Einheit</span>
              </div>
              {selectedKeys.map(k => (
                <div key={k} className="ws-key-meta-row">
                  <span className="ws-key-meta-name">{k}</span>
                  <input
                    className="ws-key-meta-input"
                    type="text"
                    placeholder="Bezeichnung…"
                    value={widget.keyLabels?.[k] || ''}
                    onChange={e => onChange({ keyLabels: { ...(widget.keyLabels || {}), [k]: e.target.value } })}
                  />
                  <input
                    className="ws-key-meta-input"
                    type="text"
                    placeholder="Einheit…"
                    value={widget.keyUnits?.[k] || ''}
                    onChange={e => onChange({ keyUnits: { ...(widget.keyUnits || {}), [k]: e.target.value } })}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Einheit (Wert-Karten mit einem Schlüssel) */}
        {typeDef.hasKeyUnit && !typeDef.noDatasource && selectedKeys.length > 0 && (
          <div className="ws-form-group">
            <input
              type="text"
              className="ws-form-input"
              id={`ws-unit-${widget.id}`}
              value={widget.keyUnits?.[selectedKeys[0]] || ''}
              onChange={e => onChange({ keyUnits: { ...(widget.keyUnits || {}), [selectedKeys[0]]: e.target.value } })}
              placeholder=" "
            />
            <label className="ws-form-label" htmlFor={`ws-unit-${widget.id}`}>Einheit</label>
          </div>
        )}

        {fields.map(field => {
          if (field.type === 'text') {
            return (
              <div key={field.key} className="ws-form-group">
                <input
                  type="text"
                  className="ws-form-input"
                  id={`ws-${field.key}-${widget.id}`}
                  value={widget.settings?.[field.key] ?? field.default ?? ''}
                  onChange={e => handleSettingChange(field.key, e.target.value)}
                  placeholder=" "
                />
                <label className="ws-form-label" htmlFor={`ws-${field.key}-${widget.id}`}>{field.label}</label>
              </div>
            );
          }

          if (field.type === 'number') {
            return (
              <div key={field.key} className="ws-form-group">
                <input
                  type="number"
                  className="ws-form-input"
                  id={`ws-${field.key}-${widget.id}`}
                  value={widget.settings?.[field.key] ?? field.default ?? 0}
                  min={field.min}
                  max={field.max}
                  step="any"
                  onChange={e => handleSettingChange(field.key, parseFloat(e.target.value) || 0)}
                />
                <label className="ws-form-label" htmlFor={`ws-${field.key}-${widget.id}`}>{field.label}</label>
              </div>
            );
          }

          if (field.type === 'checkbox') {
            return (
              <label key={field.key} className="ws-checkbox-field">
                <input
                  type="checkbox"
                  checked={widget.settings?.[field.key] ?? field.default ?? false}
                  onChange={e => handleSettingChange(field.key, e.target.checked)}
                />
                <span>{field.label}</span>
              </label>
            );
          }

          if (field.type === 'multiselect') {
            const selected = widget.settings?.[field.key] || [];
            return (
              <div key={field.key} className="ws-form-group ws-form-group--keys">
                <span className="ws-section-label">{field.label}</span>
                <div className="ws-keys-list">
                  {(field.options || []).map(opt => (
                    <label key={opt.value} className="ws-key-item">
                      <input
                        type="checkbox"
                        checked={selected.includes(opt.value)}
                        onChange={() => handleMultiselectToggle(field.key, opt.value)}
                      />
                      <span>{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            );
          }

          return null;
        })}

        {/* Zustände-Editor (State Chart) */}
        {typeDef.hasStateValues && (
          <div className="ws-states-section">
            <span className="ws-section-label">Zustände</span>
            {(widget.stateValues || []).map((sv, idx) => (
              <div key={idx} className="ws-state-row">
                <input
                  className="ws-state-input"
                  type="text"
                  placeholder="Bezeichnung…"
                  value={sv.label || ''}
                  onChange={e => {
                    const next = [...(widget.stateValues || [])];
                    next[idx] = { ...next[idx], label: e.target.value };
                    onChange({ stateValues: next });
                  }}
                />
                <input
                  className="ws-state-input"
                  type="text"
                  placeholder="Wert…"
                  value={sv.value || ''}
                  onChange={e => {
                    const next = [...(widget.stateValues || [])];
                    next[idx] = { ...next[idx], value: e.target.value };
                    onChange({ stateValues: next });
                  }}
                />
                {(widget.stateValues || []).length > 2 && (
                  <button
                    type="button"
                    className="ws-state-remove"
                    onClick={() => onChange({ stateValues: (widget.stateValues || []).filter((_, i) => i !== idx) })}
                    aria-label="Zustand entfernen"
                  ><Schließen /></button>
                )}
              </div>
            ))}
            <button
              type="button"
              className="ws-add-state-btn"
              onClick={() => onChange({ stateValues: [...(widget.stateValues || []), { label: '', value: '' }] })}
            >+ Zustand hinzufügen</button>
            {(widget.stateValues || []).length < 2 && (
              <p className="ws-hint">Mindestens 2 Zustände erforderlich.</p>
            )}
          </div>
        )}
      </div>

      {/* Position & Größe – keyboard-accessible alternative to drag/resize */}
      <div className="ws-layout-section">
        <span className="ws-section-label">Position &amp; Größe</span>
        <div className="ws-layout-grid" role="group" aria-label="Widget-Position und -Größe">
          <div className="ws-layout-field">
            <label htmlFor={`ws-x-${widget.id}`}>Spalte (X)</label>
            <input
              type="number"
              id={`ws-x-${widget.id}`}
              value={widget.layout.x}
              min={0}
              max={23}
              onChange={e => onLayoutChange({ ...widget.layout, x: Math.max(0, parseInt(e.target.value) || 0) })}
            />
          </div>
          <div className="ws-layout-field">
            <label htmlFor={`ws-y-${widget.id}`}>Zeile (Y)</label>
            <input
              type="number"
              id={`ws-y-${widget.id}`}
              value={widget.layout.y}
              min={0}
              onChange={e => onLayoutChange({ ...widget.layout, y: Math.max(0, parseInt(e.target.value) || 0) })}
            />
          </div>
          <div className="ws-layout-field">
            <label htmlFor={`ws-w-${widget.id}`}>Breite</label>
            <input
              type="number"
              id={`ws-w-${widget.id}`}
              value={widget.layout.w}
              min={2}
              max={24}
              onChange={e => onLayoutChange({ ...widget.layout, w: Math.max(2, parseInt(e.target.value) || 2) })}
            />
          </div>
          <div className="ws-layout-field">
            <label htmlFor={`ws-h-${widget.id}`}>Höhe</label>
            <input
              type="number"
              id={`ws-h-${widget.id}`}
              value={widget.layout.h}
              min={2}
              onChange={e => onLayoutChange({ ...widget.layout, h: Math.max(2, parseInt(e.target.value) || 2) })}
            />
          </div>
        </div>
      </div>

    </div>
  );
}

export default WidgetSettingsPanel;
